/**
 * QuadTree Spatial Index
 *
 * A hierarchical spatial data structure that recursively subdivides 2D space
 * into four quadrants for efficient spatial queries.
 *
 * QuadTrees reduce overlap detection from O(n) linear search to O(log n)
 * by organizing features spatially and limiting search to relevant regions.
 *
 * **For detailed QuadTree algorithm explanation, see:**
 * - [Wikipedia: Quadtree](https://en.wikipedia.org/wiki/Quadtree)
 * - [The Coding Train: Quadtree Tutorial](https://www.youtube.com/watch?v=OJxEcs0w_kE)
 *
 * @module declutterManager/quadtree
 */

/* ============ Minimal Quadtree (insert/query) ============ */

import { rectContains, rectIntersects } from './bboxUtils'
import type { BBox, QuadTreeOptions, RenderedFeature } from './types'

/**
 * Internal node of the QuadTree structure
 *
 * Each node represents a rectangular region and subdivides into 4 children
 * (NW, NE, SW, SE) when capacity is exceeded and below maxDepth.
 *
 * @internal
 */
class QuadNode {
  /** Rectangular boundary of this node */
  boundary: BBox

  /** Current depth in the tree (root = 0) */
  depth: number

  /** Maximum items before subdivision */
  capacity: number

  /** Maximum depth allowed in the tree */
  maxDepth: number

  /** Items stored at this node level */
  items: RenderedFeature[] = []

  /** Whether this node has been subdivided into children */
  divided = false

  /** Northwest child (top-left quadrant) */
  nw?: QuadNode

  /** Northeast child (top-right quadrant) */
  ne?: QuadNode

  /** Southwest child (bottom-left quadrant) */
  sw?: QuadNode

  /** Southeast child (bottom-right quadrant) */
  se?: QuadNode

  constructor(boundary: BBox, depth: number, capacity: number, maxDepth: number) {
    this.boundary = boundary
    this.depth = depth
    this.capacity = capacity
    this.maxDepth = maxDepth
  }

  /**
   * Subdivides this node into four child quadrants (NW, NE, SW, SE)
   * Called when the node exceeds capacity.
   *
   * @internal
   */
  subdivide(): void {
    if (this.divided) return
    const { x, y, w, h } = this.boundary
    const hw = w / 2,
      hh = h / 2

    this.nw = new QuadNode({ x, y, w: hw, h: hh }, this.depth + 1, this.capacity, this.maxDepth)
    this.ne = new QuadNode(
      { x: x + hw, y, w: hw, h: hh },
      this.depth + 1,
      this.capacity,
      this.maxDepth,
    )
    this.sw = new QuadNode(
      { x, y: y + hh, w: hw, h: hh },
      this.depth + 1,
      this.capacity,
      this.maxDepth,
    )
    this.se = new QuadNode(
      { x: x + hw, y: y + hh, w: hw, h: hh },
      this.depth + 1,
      this.capacity,
      this.maxDepth,
    )

    this.divided = true
  }

  /**
   * Finds which child quadrant fully contains a bbox.
   * Items that span multiple quadrants are stored at the parent level.
   *
   * @param b - Bounding box to test
   * @returns Child node that contains bbox, or null
   * @internal
   */
  private childFor(b: BBox): QuadNode | null {
    if (!this.divided) return null
    if (this.nw && rectContains(this.nw.boundary, b)) return this.nw
    if (this.ne && rectContains(this.ne.boundary, b)) return this.ne
    if (this.sw && rectContains(this.sw.boundary, b)) return this.sw
    if (this.se && rectContains(this.se.boundary, b)) return this.se
    return null
  }

  /**
   * Inserts a feature into the QuadTree
   *
   * Recursively finds the appropriate node based on spatial containment.
   * Features spanning multiple children are stored at the parent level.
   *
   * @param item - Feature to insert
   * @returns True if inserted successfully, false if outside boundary
   * @internal
   */
  insert(item: RenderedFeature): boolean {
    if (!rectIntersects(this.boundary, item.bbox, true)) return false

    if (!this.divided && (this.items.length < this.capacity || this.depth >= this.maxDepth)) {
      this.items.push(item)
      return true
    }

    if (!this.divided) this.subdivide()

    const child = this.childFor(item.bbox)
    if (child) return child.insert(item)

    this.items.push(item)
    return true
  }

  /**
   * Queries the QuadTree for features within a range
   *
   * Recursively searches for features intersecting the query range.
   * Only visits nodes that intersect, pruning large portions of the tree.
   *
   * @param range - Bounding box to query
   * @param out - Output array to accumulate results (internal use)
   * @returns Array of features intersecting the range
   * @internal
   */
  query(range: BBox, out: RenderedFeature[] = []): RenderedFeature[] {
    if (!rectIntersects(this.boundary, range, true)) return out

    for (const it of this.items) {
      if (rectIntersects(it.bbox, range, true)) out.push(it)
    }

    if (this.divided) {
      this.nw!.query(range, out)
      this.ne!.query(range, out)
      this.sw!.query(range, out)
      this.se!.query(range, out)
    }

    return out
  }
}

/**
 * QuadTree spatial index for efficient 2D range queries
 *
 * **Performance:** O(log n) queries vs O(n) linear search
 *
 * @example
 * ```typescript
 * const tree = new QuadTree(
 *   { x: 0, y: 0, w: 1920, h: 1080 },
 *   { capacity: 8, maxDepth: 8 }
 * )
 *
 * tree.insert({ bbox: { x: 100, y: 100, w: 50, h: 50 } })
 *
 * const nearby = tree.query({ x: 90, y: 90, w: 200, h: 200 })
 * ```
 */
export class QuadTree {
  /** Root node of the tree */
  root: QuadNode

  /** Maximum items per node before subdivision */
  capacity: number

  /** Maximum tree depth */
  maxDepth: number

  /**
   * Creates a new QuadTree with the specified boundary and options
   *
   * @param boundary - Initial spatial boundary of the tree
   * @param opts - Configuration options
   * @param opts.capacity - Items per node before subdivision (default: 8)
   * @param opts.maxDepth - Maximum tree depth (default: 8)
   *
   * @example
   * ```typescript
   * // Default configuration
   * const tree = new QuadTree({ x: 0, y: 0, w: 1000, h: 1000 })
   *
   * // Custom configuration for dense features
   * const denseTree = new QuadTree(
   *   { x: 0, y: 0, w: 1000, h: 1000 },
   *   { capacity: 4, maxDepth: 10 }
   * )
   * ```
   */
  constructor(boundary: BBox, opts: QuadTreeOptions = {}) {
    this.capacity = Math.max(1, opts.capacity ?? 8)
    this.maxDepth = Math.max(0, opts.maxDepth ?? 8)
    this.root = new QuadNode(boundary, 0, this.capacity, this.maxDepth)
  }

  /**
   * Returns the boundary of the QuadTree
   *
   * @returns The root node's bounding box
   */
  boundary(): BBox {
    return this.root.boundary
  }

  /**
   * Inserts a feature into the QuadTree
   *
   * @param item - Feature to insert (must have a bbox property)
   * @returns True if inserted successfully, false if outside boundary
   *
   * @example
   * ```typescript
   * const success = tree.insert({
   *   id: 'icon-123',
   *   bbox: { x: 100, y: 200, w: 38, h: 38 },
   *   data: { color: 'red' }
   * })
   * ```
   */
  insert(item: RenderedFeature): boolean {
    return this.root.insert(item)
  }

  /**
   * Queries the QuadTree for features within a bounding box range
   *
   * Returns all features whose bounding boxes intersect with the query range.
   * Much faster than linear search: O(log n + k) vs O(n).
   *
   * @param range - Bounding box to query
   * @returns Array of features intersecting the range
   *
   * @example
   * ```typescript
   * const nearby = tree.query({ x: 400, y: 400, w: 200, h: 200 })
   * console.log(`Found ${nearby.length} nearby features`)
   * ```
   */
  query(range: BBox): RenderedFeature[] {
    return this.root.query(range, [])
  }
}
